import React from "react";
import useAuth from "../hooks/useAuth.js";
import Navbar from "./Navbar.js";
import CallToAction from "./CallToAction.js";
import Footer from "./Footer.js";

function PrivacyPage() {
  const { isAuthenticated } = useAuth();

  return (
    <section id="PrivacyPage" className="bg-gray-50">
      <Navbar isAuthenticated={isAuthenticated} />

       <div className="container-fluid text-white  pl-16 py-5 shadow-sm page-header">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight">
              Privacy Policy
            </h1>
            <div className="mt-4 flex justify-center gap-4">
              <p className="text-lg text-gray-100">
                Effective: <span className="font-medium">29/11/2025</span>
              </p>
              <p className="text-lg text-gray-100">
                Last Updated: <span className="font-medium">26/09/2026</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 md:p-8 lg:p-10">
          {/* Section 1 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              1. Information Collected
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Personal Information:
                </h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Name, email, phone number, password</li>
                  <li>Where you use payment-related features, transaction or request details, payer/payee names and contact information, amounts and currencies, payment status, provider references, and review or dispute records.</li>
                  <li>Payment and payout information needed to verify transactions, process settlements, prevent fraud, and respond to support requests. Payment providers may separately collect payment credentials and verification information.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Usage Information:
                </h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Device information, logs, IP addresses</li>
                  <li>Activity logs</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Cookies:
                </h3>
                <p className="text-gray-700">
                  Ajo does not use cookies for advertising or cross-app tracking. The iOS app uses app storage for sign-in and preferences. Third-party services used for support, sign-in, or payments may use their own cookies or similar storage when you access those features.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              2. Use of Information
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide payment-related services, verify transactions, administer applicable review and settlement workflows, support reconciliation, and prevent fraud</li>
              <li>Automate group savings and thrift management</li>
              <li>Alert users about thrift activity and updates</li>
              <li>Detect and prevent fraudulent activity</li>
              <li>Enhance user experience and app performance</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              3. Sharing of Data
            </h2>
            <p className="text-gray-700 mb-4">
              We do not sell user data. We may share it as needed to provide and protect Ajo services, and as otherwise described in this policy, with:
            </p>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Payment Processors:
                </h3>
                <p className="text-gray-700">Stripe, Flutterwave, and other payment or payout providers, depending on the feature, transaction, currency, location, and provider availability. They may process transaction, payment, payout, and verification information under their own terms.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  KYC Provider:
                </h3>
                <p className="text-gray-700">Ajo and the applicable payment provider may request or process identity and payout verification information, depending on the account and transaction.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Service Providers and Other Users:
                </h3>
                <p className="text-gray-700">
                  Service providers may process information for hosting, communications, analytics, customer support, security, payment processing, and verification. Information may also be visible to other users when needed for a feature, such as group participation or transaction status.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Legal and Safety Disclosures:
                </h3>
                <p className="text-gray-700">
                  We may disclose information when required by applicable law or lawful process, or when reasonably necessary to protect users, enforce our terms and policies, investigate suspected fraud or security issues, or protect Ajo's rights and safety.
                </p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700 italic">
                <span className="font-semibold">Third-party services:</span> Payment, verification, and other providers may process information under their own privacy notices and terms. Ajo may share information with them as needed to provide the requested service and as described in this policy.
              </p>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              4. Security
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>HTTPS encryption for all data transmission</li>
              <li>PCI-DSS compliant payment partners</li>
              <li>Role-based access controls</li>
            </ul>
          </div>

          {/* Section 5 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              5. Your Rights
            </h2>
            <p className="text-gray-700 mb-4">
              You may exercise the following rights regarding your data:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Request access to your data</li>
              <li>Update or correct inaccurate information</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for data processing</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at:{" "}
              <a
                href="mailto:rights@ajoapp.ca"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                rights@ajoapp.ca
              </a>
            </p>
          </div>

          {/* Section 6 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              6. Data Retention
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Data retained up to 10 years</li>
              <li>
                After 1 year of inactivity, data is marked dormant and not
                accessible (except by court order)
              </li>
              <li>After 10 years, data is permanently deleted or anonymized</li>
            </ul>
          </div>

          {/* Section 7 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              7. International Transfers
            </h2>
            <p className="text-gray-700">
              Cross-border data storage is permitted under compliance frameworks
              including GDPR and Nigeria Data Protection Regulation.
            </p>
          </div>

          {/* Section 8 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              8. Third-Party Services
            </h2>
            <p className="text-gray-700">
              We are not liable for third-party services' privacy terms. Users
              should review the privacy policies of all third-party services
              before use.
            </p>
          </div>

          {/* Section 9 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              9. Children's Privacy
            </h2>
            <p className="text-gray-700">
              Ajo App is not intended for individuals under 18 years of age. We
              do not knowingly collect data from minors.
            </p>
          </div>

          {/* Section 10 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              10. Policy Updates
            </h2>
            <p className="text-gray-700">
              Continued use of Ajo App after policy updates constitutes
              acceptance of the new terms. Users will be notified of significant
              changes via email or in-app notification.
            </p>
          </div>

          {/* Section 11 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              11. Contact Us
            </h2>
            <p className="text-gray-700">
              For questions about this Privacy Policy:
            </p>
            <p className="mt-2">
              <a
                href="mailto:policy@ajoapp.ca"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                policy@ajoapp.ca
              </a>
            </p>
          </div>
        </div>
      </div>

      <CallToAction />
      <Footer />
    </section>
  );
}

export default PrivacyPage;